import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import { uiLocale } from "@/i18n";

interface ContactProfile {
  email: string;
  destinationWeights?: Record<string, number> | null;
  tripWeights?: Record<string, number> | null;
  attributeWeights?: Record<string, number> | null;
  stats?: {
    tripViews?: number;
    dwellSeconds?: number;
    inquiries?: number;
    orders?: number;
    quizzes?: number;
    newsletterOpens?: number;
    newsletterClicks?: number;
  } | null;
  lastEngagedAt?: string | null;
  bounceCount?: number;
  profileUpdatedAt?: string | null;
  /** What the contact chose THEMSELVES on the preference center (block 4.15 řez 6). */
  explicitPreferences?: {
    destinationSlugs: string[] | null;
    tripTypes: string[] | null;
    frequency: "weekly" | "monthly";
    pausedUntil: string | null;
  } | null;
}

function topEntries(map: Record<string, number> | null | undefined, n = 6) {
  return Object.entries(map ?? {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, n);
}

function WeightChips({ title, map }: { title: string; map: Record<string, number> | null }) {
  const { t } = useTranslation();
  const entries = topEntries(map);
  if (!entries.length) return null;
  const max = entries[0][1] || 1;
  return (
    <div>
      <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">{title}</p>
      <div className="flex flex-wrap gap-1.5">
        {entries.map(([key, w]) => (
          <span
            key={key}
            className="bg-primary-50 text-primary-700 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
            style={{ opacity: 0.45 + 0.55 * (w / max) }}
            title={t("crm.interestProfile.weight", { value: w.toFixed(2) })}
          >
            {key.replace(/^(type|difficulty|duration):/, "$1: ")}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Aggregated interest profile (newsletter Řez 1) — what this person browsed, read, asked about
 * and bought, decayed over time. Rebuilt nightly; empty until the contact has web behaviour.
 */
export default function InterestProfilePanel({ customerId }: { customerId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["customer-interest-profile", customerId],
    queryFn: () =>
      api.get<ApiResponse<ContactProfile | null>>(
        `/api/admin/customers/${customerId}/interest-profile`,
      ),
    enabled: !!customerId,
  });

  const profile = data?.data;
  if (isLoading || !profile) return null; // no behavioural data yet — don't render an empty card

  const s = profile.stats ?? {};
  const statItems: Array<[string, number | undefined]> = [
    [t("crm.interestProfile.stats.tripViews"), s.tripViews],
    [
      t("crm.interestProfile.stats.readingMin"),
      s.dwellSeconds ? Math.round(s.dwellSeconds / 60) : undefined,
    ],
    [t("crm.interestProfile.stats.inquiries"), s.inquiries],
    [t("crm.interestProfile.stats.orders"), s.orders],
    [t("crm.interestProfile.stats.nlOpens"), s.newsletterOpens],
    [t("crm.interestProfile.stats.nlClicks"), s.newsletterClicks],
  ];

  return (
    <div className="card">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Sparkles className="text-primary-600 h-5 w-5" />
          {t("crm.interestProfile.title")}
        </h2>
        {profile.profileUpdatedAt && (
          <span className="text-xs text-gray-400">
            {t("crm.interestProfile.updated", {
              date: new Date(profile.profileUpdatedAt).toLocaleDateString(uiLocale()),
            })}
          </span>
        )}
      </div>

      <div className="mb-4 grid grid-cols-3 gap-3 sm:grid-cols-6">
        {statItems.map(([label, value]) => (
          <div key={label} className="rounded-lg bg-gray-50 px-3 py-2 text-center">
            <p className="text-lg font-semibold text-gray-900">{value ?? 0}</p>
            <p className="text-[11px] text-gray-500">{label}</p>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <WeightChips
          title={t("crm.interestProfile.destinationsDerived")}
          map={profile.destinationWeights ?? null}
        />
        <WeightChips
          title={t("crm.interestProfile.tripStyleDerived")}
          map={profile.attributeWeights ?? null}
        />
        {profile.explicitPreferences && (
          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("crm.interestProfile.chosenByContact")}
            </p>
            <div className="flex flex-wrap items-center gap-1.5">
              {(profile.explicitPreferences.destinationSlugs ?? []).map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700"
                >
                  {s}
                </span>
              ))}
              {(profile.explicitPreferences.tripTypes ?? []).map((tripType) => (
                <span
                  key={tripType}
                  className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700"
                >
                  {t("crm.interestProfile.typeChip", { type: tripType })}
                </span>
              ))}
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                {profile.explicitPreferences.frequency === "monthly"
                  ? t("crm.interestProfile.monthlyDigest")
                  : t("crm.interestProfile.everyEdition")}
              </span>
              {profile.explicitPreferences.pausedUntil && (
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                  {t("crm.interestProfile.pausedUntil", {
                    date: profile.explicitPreferences.pausedUntil,
                  })}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {profile.lastEngagedAt && (
        <p className="mt-4 text-xs text-gray-400">
          {t("crm.interestProfile.lastEngaged", {
            date: new Date(profile.lastEngagedAt).toLocaleDateString(uiLocale()),
          })}
          {(profile.bounceCount ?? 0) > 0 &&
            ` · ${t("crm.interestProfile.bounces", { count: profile.bounceCount ?? 0 })}`}
        </p>
      )}
    </div>
  );
}
